import { useNavigate } from 'react-router-dom'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UserProfileDto } from '../types/UserProfileDto'
import { UserProfileRole } from '../types/UserProfile'
import { UserGender } from '../types/User'
import useUserProfilePoster from '../hooks/useUserProfilePoster'

import { ButtonAction, ButtonCheckConfirmaEx } from '@/components/Buttons'
import useUserActivate from '@/modules/users/hooks/useUserActivate'
import { formatDate, formatDateToYYYY_MM_DD } from '@/utils/utils'
import { DisplayValueFormat } from '@/components/DisplayValue'
import { DocStatusHeader } from '@/components/DocStatusHeader'
import { DocStateSpecial } from '@/enums/DocStates'
import { Info } from '@/components/MessageInfo'
import { useAuth } from '@/hooks/useAuth'
import { AppPath } from '@/enums/AppPath'
import InputField from '@/components/InputField'
import { SelectField } from '@/components/SelectField'
import { Rule, validateFields } from '@/utils/validateForm'

const UserDetail: React.FC<{ userProfile: UserProfileDto }> = ({ userProfile }) => {
  const [infoMessage, setInfoMessage] = useState('')
  const { userData, hasRole } = useAuth()
  const { userActivate, errorUserActivate } = useUserActivate()
  const { postUserProfile, errorPostUserProfile } = useUserProfilePoster()
  const [check, setCheck] = useState<boolean>(false)
  const [inactivo, setInactivo] = useState<boolean>(!!userProfile.profile.disabledAt)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [formData, setFormData] = useState<UserProfileDto>(userProfile)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [docInactive, setDocInactive] = useState({
    state: DocStateSpecial.Desactivado,
    date: userProfile.profile.disabledAt ?? '',
    cause: '',
    by: '',
  })

  const onInfoTimeout = (): void => {
    setInfoMessage('')
  }

  //Activar o dasctivar
  const onActivateClick = async () => {
    const id = userProfile.profile?._id
    const msg = await userActivate(id, inactivo, userData?.profile?._id ?? '')

    const infoMsg = inactivo ? 'Usuario activado.' : 'Usuario desactivado.'

    if (msg) {
      setInfoMessage(infoMsg)
      //volvemos a la ventana de detalle.
      const url = `/${AppPath.Users}/view/${id}`
      const date = inactivo ? '' : formatDateToYYYY_MM_DD(new Date())
      setDocInactive({ ...docInactive, date: date })

      setInactivo(!inactivo)
      setCheck(false)
      navigate(url)
    }
  }

  const onModifyClick = async () => {
    const id = userProfile.profile._id
    navigate(`/${AppPath.Users}/edit/${id}`)
  }

  const onEditBasicClick = () => {
    setIsEditing(true)
    setFormData(userProfile)
    setErrors({})
  }

  const onSaveBasicClick = async () => {
    // Validate basic fields
    const fields = [
      { name: 'user.firstname', value: formData.user.firstname, rule: Rule.REQUIRED },
      { name: 'user.lastname', value: formData.user.lastname, rule: Rule.REQUIRED },
      { name: 'profile.email', value: formData.profile.email, rule: Rule.REQUIRED },
      { name: 'user.nin', value: formData.user.nin, rule: Rule.REQUIRED },
      { name: 'user.phone', value: formData.user.phone, rule: Rule.REQUIRED },
    ]

    const validationErrors = validateFields(fields)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return
    }
    setErrors({})

    // Save the changes - set modUser to true to update user data in addition to profile data
    const res = await postUserProfile(userProfile.profile._id, userProfile.profile.roles, formData, true)

    if (res) {
      setInfoMessage('Perfil actualizado exitosamente.')
      setIsEditing(false)
      // Refresh the page to show updated data
      window.location.reload()
    }
  }

  const onCancelBasicClick = () => {
    setIsEditing(false)
    setFormData(userProfile)
    setErrors({})
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    // Handle nested fields such as 'profile.email'
    const keys = name.split('.')

    if (keys.length > 1) {
      const firstKey = keys[0] as keyof UserProfileDto
      const secondKey = keys[1]
      const nestedObject = formData[firstKey]

      if (typeof nestedObject === 'object' && nestedObject !== null) {
        setFormData({
          ...formData,
          [firstKey]: {
            ...nestedObject,
            [secondKey]: value,
          },
        })
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      })
    }
  }

  const title = inactivo ? 'Activar usuario' : 'Desactivar usuario'
  const subTitle = inactivo
    ? 'El usuario podrá iniciar sesión y realizar todas las actividades permitidas. El usuario puede ser desactivado en cualquier momento.'
    : 'Desactivar este usuario impedirá que inicie sesión y que realize cualquier acción. El usuario puede ser reactivado en cualquier momento.'
  const checkLabel = inactivo ? 'Estoy seguro de activar este usuario.' : 'Estoy seguro de desactivar ese usuario.'
  const buttonLabel = inactivo ? 'Activar' : 'Desactivar'
  const canModify = hasRole(UserProfileRole.EditProfiles)

  return (
    <div className=" mb-4">
      {inactivo && <DocStatusHeader docName="usuario" state={docInactive} />}

      <div className="text-2xl font-bold ml-4">
        {userProfile.user.firstname} {userProfile.user.lastname}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="grid grid-cols-2 mb-4">
          <DisplayValueFormat label="Fecha Ingreso" value={formatDate(userProfile.profile?.createdAt)} />

          {isEditing ? (
            <>
              <InputField
                label="Nombre"
                name="user.firstname"
                value={formData?.user.firstname}
                type="text"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="Apellido"
                name="user.lastname"
                value={formData?.user.lastname}
                type="text"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="Email"
                name="profile.email"
                value={formData?.profile.email}
                type="email"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="C.I."
                name="user.nin"
                value={formData?.user?.nin}
                type="text"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <InputField
                label="Teléfono"
                name="user.phone"
                value={formData?.user?.phone}
                type="tel"
                handleInputChange={handleInputChange}
                errors={errors}
              />
              <SelectField
                label="Género"
                name="user.gender"
                value={formData?.user?.gender}
                options={UserGender}
                handleInputChange={handleInputChange}
              />
            </>
          ) : (
            <>
              <DisplayValueFormat label="Nombre" value={userProfile.user?.firstname} />
              <DisplayValueFormat label="Apellido" value={userProfile.user?.lastname} />
              <DisplayValueFormat label="Email" value={userProfile.profile?.email} />
              <DisplayValueFormat label="C.I." value={userProfile.user?.nin} />
              <DisplayValueFormat label="Teléfono" value={userProfile.user?.phone} />
              <DisplayValueFormat label="Género" value={t(userProfile.user?.gender)} />
              <DisplayValueFormat label="Cargo" value={t(userProfile.profile?.job)} />
            </>
          )}
        </div>

        <div className="list-disc list-inside">
          {userProfile.profile.roles.map((role: string, index: number) => (
            <li key={`${index} ${role}`}>{t(role)}</li>
          ))}
        </div>
      </div>
      {canModify && (
        <div>
          {!isEditing ? (
            <div className="flex justify-center mt-4 space-x-4">
              <ButtonAction label="Editar Perfil Básico" handleButtonClick={onEditBasicClick} />
              <ButtonAction label="Modificar..." handleButtonClick={onModifyClick} />
            </div>
          ) : (
            <div className="flex justify-center mt-4 space-x-4">
              <ButtonAction label="Guardar" handleButtonClick={onSaveBasicClick} />
              <ButtonAction label="Cancelar" handleButtonClick={onCancelBasicClick} />
            </div>
          )}

          <ButtonCheckConfirmaEx
            title={title}
            subTitle={subTitle}
            checkLabel={checkLabel}
            buttonLabel={buttonLabel}
            handleCrearClick={onActivateClick}
            check={check}
            setCheck={setCheck}
          />
        </div>
      )}
      <Info message={infoMessage} trigger={Date.now()} onTimeout={onInfoTimeout} noCenter={true} />

      <div>{errorUserActivate?.message}</div>
      <div>{errorPostUserProfile?.message}</div>
    </div>
  )
}

export default UserDetail
